const BASE_URL = 'https://digian.ai';
const OG_IMAGE = 'https://digian.ai/og-image.png';

export const SEO = {
  default: {
    title: 'Digian – AI Voice Agent & Business Automation Platform',
    description: 'Digian is an enterprise AI voice agent and workflow automation platform. Automate IT support, customer care, and business processes 24/7 in 30+ languages.',
    image: OG_IMAGE,
    url: BASE_URL,
  },

  // Core pages
  home: {
    title: 'Digian – AI Voice Agent for IT Support & Customer Care',
    description: 'Digian\'s AI voice agent resolves 70% of IT support and customer care calls automatically, 24/7, in 30+ languages. Deploy in days — not months.',
    url: `${BASE_URL}/`,
  },
  products: {
    title: 'Digian Products – AI Voice Agent, Customer Care & Automation',
    description: 'Explore Digian\'s full AI product suite: AI Voice Agent, Customer Care, Web Chat, Workflow Automation, Compliance Studio, and Analytics Chat Agents.',
    url: `${BASE_URL}/products`,
  },
  about: {
    title: 'About Digian – Enterprise AI Voice Agent Company',
    description: 'Digian builds enterprise AI voice agents and automation tools that transform IT support and customer care operations. Learn about our mission.',
    url: `${BASE_URL}/about`,
  },
  contact: {
    title: 'Contact Digian – Book an AI Voice Agent Demo',
    description: 'Talk to the Digian team. Book a live demo of our AI voice agent, get pricing, or ask about integrations with your existing systems.',
    url: `${BASE_URL}/contact`,
  },
  blog: {
    title: 'Digian Blog – AI Voice Agent & Automation Insights',
    description: 'Guides, case studies, and insights on AI voice agents, IT automation, and enterprise customer care from the Digian team.',
    url: `${BASE_URL}/blog`,
  },
  careers: {
    title: 'Careers at Digian – Join Our AI Voice Agent Team',
    description: 'Help build the future of AI voice agents and enterprise automation. View open roles at Digian.',
    url: `${BASE_URL}/careers`,
  },
  caseStudies: {
    title: 'Digian Case Studies – AI Voice Agent Results',
    description: 'See how enterprises use Digian\'s AI voice agent to automate IT support, customer care, and business workflows. Real results, real businesses.',
    url: `${BASE_URL}/case-studies`,
  },
  demo: {
    title: 'Request a Digian Demo – See the AI Voice Agent Live',
    description: 'See Digian\'s AI voice agent in action. Book a personalised demo and discover how voice AI automation can transform your support operations.',
    url: `${BASE_URL}/demo`,
  },
  docs: {
    title: 'Digian Docs – AI Voice Agent Integration Guide',
    description: 'Technical documentation, API references, and integration guides for deploying Digian\'s AI voice agent and automation platform.',
    url: `${BASE_URL}/docs`,
  },

  // Product pages
  aiVoiceAgent: {
    title: 'AI Voice Agent – Digian | Automate Calls 24/7',
    description: 'Digian\'s AI voice agent handles inbound and outbound calls 24/7 with natural conversation. Automate IT helpdesk and customer care voice support at enterprise scale.',
    url: `${BASE_URL}/ai-voice-agent`,
  },
  aiCustomerCare: {
    title: 'AI Customer Care – Digian | Voice, Chat & Email Automation',
    description: 'Automate customer care across voice, chat, and email with Digian\'s AI. Instant, accurate support with seamless human handoff when needed.',
    url: `${BASE_URL}/ai-customer-care`,
  },
  aiWebChat: {
    title: 'AI Web Chat – Digian | Intelligent Chat Widget for Websites',
    description: 'Add an AI chat agent to your website that qualifies leads, resolves queries, and books demos — all without human involvement.',
    url: `${BASE_URL}/ai-web-chat`,
  },
  aiWorkflowAutomation: {
    title: 'AI Workflow Automation – Digian | No-Code Process Automation',
    description: 'Automate business processes end-to-end with Digian\'s AI workflow builder. 50+ connectors for ServiceNow, Salesforce, Jira, Azure AD, and more.',
    url: `${BASE_URL}/ai-workflow-automation`,
  },
  aiComplianceStudio: {
    title: 'AI Compliance Studio – Digian | Governance for AI Agents',
    description: 'Monitor, audit, and enforce compliance across all AI voice and chat interactions. Real-time PII masking, policy controls, and full audit trails.',
    url: `${BASE_URL}/ai-compliance-studio`,
  },
  analyticsChatAgents: {
    title: 'Analytics Chat Agents – Digian | Query Data in Plain English',
    description: 'Ask questions about your business data in plain English. Digian\'s analytics chat agents query Snowflake, Databricks, BigQuery, and more — no SQL needed.',
    url: `${BASE_URL}/analytics-chat-agents`,
  },

  // Use-case pages
  useCaseOrderStatus: {
    title: 'AI WISMO & Order Status Voice Agent – Digian',
    description: 'Automate "Where is my order?" calls with Digian\'s AI voice agent. Reduce WISMO call volume by up to 80% with instant automated order status updates.',
    url: `${BASE_URL}/use-cases/order-status`,
  },
  useCasePasswordReset: {
    title: 'AI Password Reset Automation – Digian Voice Agent',
    description: 'Automate password reset requests via voice or chat, 24/7. Reduce IT helpdesk tickets instantly with Digian\'s AI voice agent.',
    url: `${BASE_URL}/use-cases/password-reset`,
  },
  useCaseBillingPayments: {
    title: 'AI Billing & Payments Voice Agent – Digian',
    description: 'Automate billing enquiries and payment support with Digian\'s AI voice agent. Instant answers, zero wait time, full system integration.',
    url: `${BASE_URL}/use-cases/billing-payments`,
  },
  useCaseEmployeeOnboarding: {
    title: 'AI Employee Onboarding Automation – Digian',
    description: 'Automate employee onboarding workflows with Digian\'s AI. Handle documentation, access provisioning, and common HR queries automatically.',
    url: `${BASE_URL}/use-cases/employee-onboarding`,
  },
  useCaseIdentityVerification: {
    title: 'AI Identity Verification Voice Agent – Digian',
    description: 'Automate identity verification via voice with Digian\'s AI agent. Secure, compliant, and fully audited — no human needed for standard checks.',
    url: `${BASE_URL}/use-cases/identity-verification`,
  },
  useCaseSoftwareProvisioning: {
    title: 'AI Software Provisioning Automation – Digian',
    description: 'Automate software access requests and provisioning with Digian\'s AI agents. Faster delivery, fewer helpdesk tickets, full audit trail.',
    url: `${BASE_URL}/use-cases/software-provisioning`,
  },
  useCaseCaseTriage: {
    title: 'AI Case Triage & Routing Voice Agent – Digian',
    description: 'Automatically classify, prioritise, and route support cases with Digian\'s AI voice agent. Faster resolution, lower handling costs.',
    url: `${BASE_URL}/use-cases/case-triage`,
  },
  useCaseGroupsManagement: {
    title: 'AI Groups & Access Management Automation – Digian',
    description: 'Automate user group requests and access management with Digian\'s AI. Integrates with Azure AD, Okta, and Active Directory.',
    url: `${BASE_URL}/use-cases/groups-management`,
  },
  useCaseAppointmentScheduling: {
    title: 'AI Appointment Scheduling Voice Agent – Digian',
    description: 'Book, reschedule, and manage appointments via voice or chat with Digian\'s AI agent. Available 24/7, no human needed.',
    url: `${BASE_URL}/use-cases/appointment-scheduling`,
  },
  useCaseHardwareSupport: {
    title: 'AI Hardware Support & IT Field Service – Digian',
    description: 'Automate hardware fault logging, triage, and field dispatch with Digian\'s AI voice agent. Faster resolution for IT hardware support tickets.',
    url: `${BASE_URL}/use-cases/hardware-support`,
  },

  // Legal pages
  privacy: {
    title: 'Privacy Policy – Digian',
    description: 'How Digian collects, uses, and protects your personal data. Read our full privacy policy.',
    url: `${BASE_URL}/privacy`,
  },
  terms: {
    title: 'Terms of Service – Digian',
    description: 'Terms governing use of Digian\'s AI voice agent and automation platform.',
    url: `${BASE_URL}/terms`,
  },
  cookiePolicy: {
    title: 'Cookie Policy – Digian',
    description: 'How Digian uses cookies and similar tracking technologies on digian.ai.',
    url: `${BASE_URL}/cookie-policy`,
  },
  dpa: {
    title: 'Data Processing Agreement – Digian',
    description: 'Digian\'s DPA for customers subject to GDPR and other data protection regulations.',
    url: `${BASE_URL}/dpa`,
  },
  accessibility: {
    title: 'Accessibility Statement – Digian',
    description: 'Digian\'s commitment to making our AI voice agent platform accessible to everyone.',
    url: `${BASE_URL}/accessibility`,
  },

  // Auth pages (noindex)
  login: {
    title: 'Login – Digian',
    description: 'Log in to your Digian account.',
    url: `${BASE_URL}/login`,
    noIndex: true,
  },
  register: {
    title: 'Register – Digian',
    description: 'Create your Digian account.',
    url: `${BASE_URL}/register`,
    noIndex: true,
  },
};
